import { AxiosInstance, AxiosRequestConfig } from 'axios'

import { baseService } from './base.service'
import {
  BulkCreateQuestionsDto,
  BulkCreateQuestionsResponse,
  CreateOnlineTestDto,
  CreateTestAnswerDto,
  CreateTestQuestionDto,
  GenerateTestQuestionsDto,
  OnlineTestAiJobResponse,
  OnlineTestAiJobStatusResponse,
  RegenerateTestQuestionsDto,
  ReportTestViolationDto,
  SubmitTestAnswerDto,
  SubmitTestAttemptDto,
  UpdateOnlineTestDto,
  UpdateTestAnswerDto,
  UpdateTestQuestionDto,
} from './dto/online-test-dto.interface'
import { IBaseService } from './interfaces/base-service.interface'

import { QueryParams } from '@/types/base.type'
import { ListResponse } from '@/types/list.type'
import {
  ActiveAttemptDetail,
  AdminAttemptReview,
  AttemptReview,
  CourseAssignmentSummary,
  OnlineTest,
  OnlineTestId,
  QuestionListItem,
  StartedTestAttempt,
  TestAnswer,
  TestAssignment,
  TestAttempt,
  TestQuestion,
  TestResultsDashboard,
  TestUserAssignment,
  TestViolationResponse,
} from '@/types/online-test.type'

type ResultsFilters = { page?: number; limit?: number; isPassed?: boolean; sort?: string }
type PaginationParams = { page?: number; limit?: number }

export class OnlineTestService {
  private static instance: OnlineTestService
  private readonly api: AxiosInstance

  private constructor(private readonly baseService: IBaseService) {
    this.api = this.baseService.getClient()
  }

  public static getInstance(baseService: IBaseService): OnlineTestService {
    if (!OnlineTestService.instance) {
      OnlineTestService.instance = new OnlineTestService(baseService)
    }
    return OnlineTestService.instance
  }



  async createTest<T = OnlineTest>(data: CreateOnlineTestDto, options?: AxiosRequestConfig) {
    const response = await this.api.post<T>('/online-test', data, options)
    return response.data
  }

  async getTests<T = ListResponse<OnlineTest>>(query?: QueryParams, options?: AxiosRequestConfig) {
    const response = await this.api.get<T>('/online-test', {
      ...options,
      params: { pageNumber: query?.pageNumber, pageSize: query?.pageSize, search: query?.search },
    })
    return response.data
  }

  async getTestById<T = OnlineTest>(id: OnlineTestId, options?: AxiosRequestConfig) {
    const response = await this.api.get<T>(`/online-test/${id}`, options)
    return response.data
  }

  async updateTest<T = OnlineTest>(id: OnlineTestId, data: UpdateOnlineTestDto, options?: AxiosRequestConfig) {
    const response = await this.api.put<T>(`/online-test/${id}`, data, options)
    return response.data
  }

  async deleteTest<T = { message: string }>(id: OnlineTestId, options?: AxiosRequestConfig) {
    const response = await this.api.delete<T>(`/online-test/${id}`, options)
    return response.data
  }

  async publishTest<T = OnlineTest>(id: OnlineTestId, options?: AxiosRequestConfig) {
    const response = await this.api.post<T>(`/online-test/${id}/publish`, {}, options)
    return response.data
  }

  async unpublishTest<T = OnlineTest>(id: OnlineTestId, options?: AxiosRequestConfig) {
    const response = await this.api.post<T>(`/online-test/${id}/unpublish`, {}, options)
    return response.data
  }

  async duplicateTest<T = OnlineTest>(id: OnlineTestId, options?: AxiosRequestConfig) {
    const response = await this.api.post<T>(`/online-test/${id}/duplicate`, {}, options)
    return response.data
  }

  async getQuestions(
    testId: OnlineTestId,
    params?: { page?: number; limit?: number; search?: string },
    options?: AxiosRequestConfig
  ) {
    const response = await this.api.get<ListResponse<QuestionListItem>>(
      `/online-test/${testId}/question`,
      { ...options, params }
    )
    return response.data
  }

  async getQuestionById(testId: OnlineTestId, questionId: OnlineTestId, options?: AxiosRequestConfig) {
    const response = await this.api.get<TestQuestion>(`/online-test/${testId}/question/${questionId}`, options)
    return response.data
  }

  async reorderQuestions(testId: OnlineTestId, orderedIds: string[], startIndex = 0, options?: AxiosRequestConfig) {
    const response = await this.api.patch<{ message: string }>(
      `/online-test/${testId}/question/reorder`,
      { orderedIds, startIndex },
      options
    )
    return response.data
  }

  async createQuestion<T = OnlineTest>(testId: OnlineTestId, data: CreateTestQuestionDto, options?: AxiosRequestConfig) {
    const response = await this.api.post<T>(`/online-test/${testId}/question`, data, options)
    return response.data
  }

  async bulkCreateQuestions<T = BulkCreateQuestionsResponse>(
    testId: OnlineTestId,
    data: BulkCreateQuestionsDto,
    options?: AxiosRequestConfig
  ) {
    const response = await this.api.post<T>(`/online-test/${testId}/question/bulk`, data, options)
    return response.data
  }

  async generateQuestions<T = OnlineTestAiJobResponse>(
    testId: OnlineTestId,
    data: GenerateTestQuestionsDto,
    options?: AxiosRequestConfig
  ) {
    const response = await this.api.post<T>(`/online-test/${testId}/question/generate`, data, options)
    return response.data
  }

  async regenerateQuestions<T = OnlineTestAiJobResponse>(
    testId: OnlineTestId,
    data: RegenerateTestQuestionsDto,
    options?: AxiosRequestConfig
  ) {
    const response = await this.api.post<T>(`/online-test/${testId}/question/regenerate`, data, options)
    return response.data
  }

  async getAiQuestionJob<T = OnlineTestAiJobStatusResponse>(
    testId: OnlineTestId,
    jobId: string,
    options?: AxiosRequestConfig
  ) {
    const response = await this.api.get<T>(`/online-test/${testId}/question/ai-jobs/${jobId}`, options)
    return response.data
  }

  async updateQuestion<T = TestQuestion>(
    testId: OnlineTestId,
    questionId: OnlineTestId,
    data: UpdateTestQuestionDto,
    options?: AxiosRequestConfig
  ) {
    const response = await this.api.put<T>(`/online-test/${testId}/question/${questionId}`, data, options)
    return response.data
  }

  async deleteQuestion<T = { message: string }>(testId: OnlineTestId, questionId: OnlineTestId, options?: AxiosRequestConfig) {
    const response = await this.api.delete<T>(`/online-test/${testId}/question/${questionId}`, options)
    return response.data
  }

  async createAnswer<T = TestAnswer>(
    testId: OnlineTestId,
    questionId: OnlineTestId,
    data: CreateTestAnswerDto,
    options?: AxiosRequestConfig
  ) {
    const response = await this.api.post<T>(`/online-test/${testId}/question/${questionId}/answer`, data, options)
    return response.data
  }

  async updateAnswer<T = OnlineTest>(
    testId: OnlineTestId,
    questionId: OnlineTestId,
    answerId: OnlineTestId,
    data: UpdateTestAnswerDto,
    options?: AxiosRequestConfig
  ) {
    const response = await this.api.put<T>(`/online-test/${testId}/question/${questionId}/answer/${answerId}`, data, options)
    return response.data
  }

  async deleteAnswer<T = { message: string }>(
    testId: OnlineTestId,
    questionId: OnlineTestId,
    answerId: OnlineTestId,
    options?: AxiosRequestConfig
  ) {
    const response = await this.api.delete<T>(`/online-test/${testId}/question/${questionId}/answer/${answerId}`, options)
    return response.data
  }

  async assignUser<T = TestAssignment>(testId: OnlineTestId, userId: number, options?: AxiosRequestConfig) {
    const response = await this.api.post<T>(`/online-test/${testId}/assign`, { userId }, options)
    return response.data
  }

  async assignCourse<T = CourseAssignmentSummary>(testId: OnlineTestId, courseId: number, options?: AxiosRequestConfig) {
    const response = await this.api.post<T>(`/online-test/${testId}/assign/course`, { courseId }, options)
    return response.data
  }

  async removeAssignment<T = { message: string }>(testId: OnlineTestId, userId: number, options?: AxiosRequestConfig) {
    const response = await this.api.delete<T>(`/online-test/${testId}/assign/${userId}`, options)
    return response.data
  }

  async getAssignments<T = ListResponse<TestAssignment>>(testId: OnlineTestId, params?: PaginationParams, options?: AxiosRequestConfig) {
    const response = await this.api.get<T>(`/online-test/${testId}/assignments`, { ...options, params })
    return response.data
  }

  async getUserAssignments<T = ListResponse<TestUserAssignment>>(userId: number, params?: PaginationParams, options?: AxiosRequestConfig) {
    const response = await this.api.get<T>(`/online-test/user/${userId}/assignments`, { ...options, params })
    return response.data
  }

  async getPublicTests<T = ListResponse<OnlineTest>>(params?: PaginationParams, options?: AxiosRequestConfig) {
    const response = await this.api.get<T>('/online-test/public-tests', { ...options, params })
    return response.data
  }

  async getMyTests<T = ListResponse<OnlineTest>>(params?: PaginationParams, options?: AxiosRequestConfig) {
    const response = await this.api.get<T>('/online-test/my-tests', { ...options, params })
    return response.data
  }

  async getTestDetail<T = OnlineTest>(testId: OnlineTestId, options?: AxiosRequestConfig) {
    const response = await this.api.get<T>(`/online-test/${testId}/detail`, options)
    return response.data
  }

  async startAttempt<T = StartedTestAttempt>(testId: OnlineTestId, options?: AxiosRequestConfig) {
    const response = await this.api.post<T>(`/online-test/${testId}/attempt`, {}, options)
    return response.data
  }

  async getActiveAttemptDetail<T = ActiveAttemptDetail>(
    testId: OnlineTestId,
    attemptId: OnlineTestId,
    options?: AxiosRequestConfig
  ) {
    const response = await this.api.get<T>(`/online-test/${testId}/attempt/${attemptId}/detail`, options)
    return response.data
  }

  async submitAnswer<T = { saved: boolean }>(
    testId: OnlineTestId,
    attemptId: OnlineTestId,
    data: SubmitTestAnswerDto,
    options?: AxiosRequestConfig
  ) {
    const response = await this.api.post<T>(`/online-test/${testId}/attempt/${attemptId}/answer`, data, options)
    return response.data
  }

  async reportViolation<T = TestViolationResponse>(
    testId: OnlineTestId,
    attemptId: OnlineTestId,
    data: ReportTestViolationDto,
    options?: AxiosRequestConfig
  ) {
    const response = await this.api.post<T>(`/online-test/${testId}/attempt/${attemptId}/violation`, data, options)
    return response.data
  }

  async submitAttempt<T = AttemptReview>(
    testId: OnlineTestId,
    attemptId: OnlineTestId,
    data?: SubmitTestAttemptDto,
    options?: AxiosRequestConfig,
  ) {
    const response = await this.api.post<T>(
      `/online-test/${testId}/attempt/${attemptId}/submit`,
      data ?? {},
      options,
    )
    return response.data
  }

  async getMyAttempts<T = TestAttempt[]>(testId: OnlineTestId, options?: AxiosRequestConfig) {
    const response = await this.api.get<T>(`/online-test/${testId}/attempts`, options)
    return response.data
  }

  async reviewAttempt<T = AttemptReview>(testId: OnlineTestId, attemptId: OnlineTestId, options?: AxiosRequestConfig, page?: number, limit?: number) {
    const response = await this.api.get<T>(`/online-test/${testId}/attempt/${attemptId}/review`, {
      ...options,
      params: { ...(page != null ? { page } : {}), ...(limit != null ? { limit } : {}) },
    })
    return response.data
  }

  async getResults<T = TestResultsDashboard>(testId: OnlineTestId, filters?: ResultsFilters, options?: AxiosRequestConfig) {
    const response = await this.api.get<T>(`/online-test/${testId}/results`, { ...options, params: filters })
    return response.data
  }

  async getAttemptDetail<T = AdminAttemptReview>(testId: OnlineTestId, attemptId: OnlineTestId, options?: AxiosRequestConfig, page?: number, limit?: number) {
    const response = await this.api.get<T>(`/online-test/${testId}/results/${attemptId}`, {
      ...options,
      params: { ...(page != null ? { page } : {}), ...(limit != null ? { limit } : {}) },
    })
    return response.data
  }

  async exportResults(testId: OnlineTestId, options?: AxiosRequestConfig) {
    const response = await this.api.get(`/online-test/${testId}/results/export`, {
      ...options,
      responseType: 'blob',
    })
    return response.data
  }
}

const onlineTestService = OnlineTestService.getInstance(baseService)

export default onlineTestService
